import { darken, lighten } from './colorUtils';
import { BrandingColors } from './types';

/**
 * Berry seam — palette input.
 *
 * Berry's `themes/palette.tsx` exposes `buildPalette(presetColor)` which turns a
 * static preset (theme1..theme6) into `{ light, dark }` palette options. We do
 * NOT fork that file: the app passes Berry's own `buildPalette` output in as the
 * BASE (see `BerrySeam.buildBasePalette` in ZercodeThemeProvider), and this
 * function overlays the TENANT'S real brand colors from the Control Plane
 * branding response on top — so `buildPalette`'s input is effectively the
 * tenant branding, not a hard-coded preset [STD-FE].
 */

type AnyPalette = Record<string, unknown>;

export interface SchemePalettes {
  light: AnyPalette;
  dark: AnyPalette;
}

/** Berry's palette color contract for primary/secondary: light/main/dark + 200/800 */
export function brandColorSet(main: string, light?: string, dark?: string) {
  return {
    light: light ?? lighten(main, 0.88),
    main,
    dark: dark ?? darken(main, 0.12),
    200: lighten(main, 0.5),
    800: darken(main, 0.3)
  };
}

function overlay(base: AnyPalette, key: 'primary' | 'secondary', set: ReturnType<typeof brandColorSet>): AnyPalette {
  const existing = (base[key] as AnyPalette | undefined) ?? {};
  return { ...base, [key]: { ...existing, ...set } };
}

/**
 * Overlay tenant brand colors on Berry's base palettes.
 * For the dark scheme Berry keeps `main` on the 200-weight for contrast; we
 * follow the same convention.
 */
export function applyBrandingToPalettes(base: SchemePalettes, colors: BrandingColors): SchemePalettes {
  const primary = brandColorSet(colors.primaryMain, colors.primaryLight, colors.primaryDark);
  const secondary = brandColorSet(colors.secondaryMain, colors.secondaryLight, colors.secondaryDark);

  const light = overlay(overlay(base.light, 'primary', primary), 'secondary', secondary);

  const darkPrimary = { ...primary, light: lighten(colors.primaryMain, 0.35), main: primary[200] };
  const darkSecondary = { ...secondary, light: lighten(colors.secondaryMain, 0.35), main: secondary[200] };
  const dark = overlay(overlay(base.dark, 'primary', darkPrimary), 'secondary', darkSecondary);

  return { light, dark };
}
